/**
 * LoRA Wizard State Management Hook
 * Manages all wizard state and actions using useState
 */

import { useState, useCallback, useRef, useEffect } from "react";
import type {
  LoraWizardState,
  LoraWizardActions,
  ModelId,
  TrainingSettings,
  AugmentationSettings,
  ReferenceImage,
  TestResult,
  EpochStatus,
  EpochSnapshot,
  TrainingSummary,
} from "./types";
import { DEFAULT_SETTINGS, SLIDE_CONFIGS } from "./types";

// Highest valid slide index. Derived from SLIDE_CONFIGS so adding a slide
// (e.g. the real-mode PREPARE step, which shifted LOCK from 6 to 7) can never
// again strand the last slide behind a stale hard-coded bound.
const MAX_SLIDE_INDEX = SLIDE_CONFIGS.length - 1;

// Generate unique ID
const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Mock test prompts
const MOCK_PROMPTS = [
  "wide cinematic shot, rainy alley, neon spill",
  "close-up portrait, dramatic side lighting",
  "full body shot, bright daylight, urban setting",
  "medium shot, golden hour, outdoor cafe",
];

// Placeholder images for epoch snapshots
const PLACEHOLDER_IMAGES = [
  "/assets/placeholder_1.jpg",
  "/assets/placeholder_2.jpeg",
  "/assets/placeholder_3.jpg",
  "/assets/placeholder_4.jpg",
];

// Get random set of 4 preview images for a snapshot
function getSnapshotImages(): string[] {
  // Shuffle and return 4 images (with repetition allowed for variety)
  return Array.from(
    { length: 4 },
    () =>
      PLACEHOLDER_IMAGES[Math.floor(Math.random() * PLACEHOLDER_IMAGES.length)],
  );
}

// Initial state
const initialState: LoraWizardState = {
  currentSlide: 0,
  isLocked: false,
  selectedModel: null,
  references: [],
  settings: DEFAULT_SETTINGS,
  isTraining: false,
  trainingProgress: null,
  testResults: [],
  trainingSummary: null,
  isComplete: false,
};

export function useLoraWizardState(
  castName: string = "unknown",
): LoraWizardState & LoraWizardActions {
  const [state, setState] = useState<LoraWizardState>(initialState);
  const trainingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup training interval on unmount
  useEffect(() => {
    return () => {
      if (trainingIntervalRef.current) {
        clearInterval(trainingIntervalRef.current);
      }
    };
  }, []);

  // Navigation actions
  const nextSlide = useCallback(() => {
    setState((prev) => {
      const config = SLIDE_CONFIGS[prev.currentSlide];
      if (
        config &&
        config.canAdvance(prev) &&
        prev.currentSlide < MAX_SLIDE_INDEX
      ) {
        return { ...prev, currentSlide: prev.currentSlide + 1 };
      }
      return prev;
    });
  }, []);

  const prevSlide = useCallback(() => {
    setState((prev) => {
      const config = SLIDE_CONFIGS[prev.currentSlide];
      if (config && config.canGoBack(prev) && prev.currentSlide > 0) {
        return { ...prev, currentSlide: prev.currentSlide - 1 };
      }
      return prev;
    });
  }, []);

  const goToSlide = useCallback((index: number) => {
    setState((prev) => {
      // Not allowed while locked.
      if (prev.isLocked) return prev;
      // Allow any valid slide index (0..MAX_SLIDE_INDEX). Forward navigation is
      // required by LoraWizard.nextSlide, which performs its own canAdvance gate
      // (using the real-mode selection/state) BEFORE calling goToSlide — so
      // re-restricting to backward-only here silently broke the Next button.
      // Backward jumps from the step indicator remain gated by `isClickable`.
      if (index >= 0 && index <= MAX_SLIDE_INDEX) {
        return { ...prev, currentSlide: index };
      }
      return prev;
    });
  }, []);

  // Slide 1: Model selection
  const selectModel = useCallback((modelId: ModelId) => {
    setState((prev) => ({ ...prev, selectedModel: modelId }));
  }, []);

  // Slide 2: References
  const addReferences = useCallback((files: File[]) => {
    setState((prev) => {
      const newRefs: ReferenceImage[] = files.map((file) => ({
        id: generateId(),
        file,
        previewUrl: URL.createObjectURL(file),
        description:
          "Auto-generated description placeholder. A detailed caption describing the visual content, lighting, pose, and expression in this reference image.",
        hasTriggerWord: false,
        // Randomly mark ~30% of images as needing upscale (simulating low-res images)
        needsUpscale: Math.random() < 0.3,
      }));
      // Cap at 25 images
      const combined = [...prev.references, ...newRefs].slice(0, 25);
      return { ...prev, references: combined };
    });
  }, []);

  const removeReference = useCallback((id: string) => {
    setState((prev) => {
      const ref = prev.references.find((r) => r.id === id);
      if (ref) {
        URL.revokeObjectURL(ref.previewUrl);
      }
      return {
        ...prev,
        references: prev.references.filter((r) => r.id !== id),
      };
    });
  }, []);

  const updateReferenceDescription = useCallback(
    (id: string, description: string) => {
      setState((prev) => ({
        ...prev,
        references: prev.references.map((ref) =>
          ref.id === id ? { ...ref, description } : ref,
        ),
      }));
    },
    [],
  );

  const toggleReferenceTriggerWord = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      references: prev.references.map((ref) =>
        ref.id === id ? { ...ref, hasTriggerWord: !ref.hasTriggerWord } : ref,
      ),
    }));
  }, []);

  // Slide 3: Settings
  const updateSettings = useCallback((settings: Partial<TrainingSettings>) => {
    setState((prev) => ({
      ...prev,
      settings: { ...prev.settings, ...settings },
    }));
  }, []);

  const updateAugmentation = useCallback(
    (augmentation: Partial<AugmentationSettings>) => {
      setState((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          augmentation: { ...prev.settings.augmentation, ...augmentation },
        },
      }));
    },
    [],
  );

  // Slide 4-5: Training
  const startTraining = useCallback(() => {
    setState((prev) => {
      const epochs: EpochStatus[] = Array.from(
        { length: prev.settings.epochs },
        (_, i) => ({
          epoch: i + 1,
          status: i === 0 ? "running" : "pending",
        }),
      );

      const snapshotInterval =
        prev.settings.snapshotEvery === "final"
          ? prev.settings.epochs
          : prev.settings.snapshotEvery;

      return {
        ...prev,
        isTraining: true,
        isLocked: true,
        currentSlide: 4, // Move to training slide
        trainingProgress: {
          currentEpoch: 1,
          totalEpochs: prev.settings.epochs,
          timeRemaining: calculateTimeRemaining(prev.settings.epochs, 0),
          epochs,
          snapshots: [],
          snapshotInterval,
          startedAt: new Date(),
        },
      };
    });

    // Simulate training progress (2 seconds per epoch for demo)
    const epochDuration = 2000;
    trainingIntervalRef.current = setInterval(() => {
      setState((prev) => {
        if (!prev.trainingProgress || !prev.isTraining) {
          if (trainingIntervalRef.current) {
            clearInterval(trainingIntervalRef.current);
          }
          return prev;
        }

        const nextEpoch = prev.trainingProgress.currentEpoch + 1;
        const totalEpochs = prev.trainingProgress.totalEpochs;

        // Training complete
        if (nextEpoch > totalEpochs) {
          if (trainingIntervalRef.current) {
            clearInterval(trainingIntervalRef.current);
          }

          // Add final snapshot
          const finalSnapshot: EpochSnapshot = {
            epoch: totalEpochs,
            timestamp: new Date(),
            loss: Math.random() * 0.03 + 0.02, // Lowest loss at end
            previewImages: getSnapshotImages(),
          };
          const finalSnapshots = prev.trainingProgress.snapshots.some(
            (s) => s.epoch === totalEpochs,
          )
            ? prev.trainingProgress.snapshots
            : [...prev.trainingProgress.snapshots, finalSnapshot];

          // Generate test results
          const testResults: TestResult[] = MOCK_PROMPTS.map((prompt, i) => ({
            id: generateId(),
            promptNumber: i + 1,
            prompt,
            imageUrl: PLACEHOLDER_IMAGES[i % PLACEHOLDER_IMAGES.length],
            score: Math.floor(Math.random() * 15) + 80, // 80-95
            accepted: false,
          }));

          // Generate training summary (demo placeholder - not a real training contract)
          const triggerWord = `${castName.toLowerCase().replace(/\s+/g, "_").slice(0, 3)}_c_v1`;
          const trainingSummary: TrainingSummary = {
            datasetSize: prev.references.length,
            lightingConditions: Math.min(
              6,
              Math.floor(prev.references.length / 4),
            ),
            triggerWord,
            baseModel: prev.selectedModel || "flux-1-dev",
            rank: prev.settings.rank,
            epochs: prev.settings.epochs,
            finalLoss: Math.random() * 0.05 + 0.06, // 0.06-0.11
            likenessScore: Math.floor(Math.random() * 12) + 82, // 82-94
            trainedAt: new Date(),
            gpuMinutes: Math.floor(prev.settings.epochs * 1.2 + 10),
            cost:
              Math.round((prev.settings.epochs * 1.2 + 10) * 0.014 * 100) / 100,
            artifactFilename: `${triggerWord.replace("_c_", "_lora_")}.safetensors`,
            artifactSize: `${Math.floor(Math.random() * 50 + 200)} MB`,
          };

          return {
            ...prev,
            isTraining: false,
            testResults,
            trainingSummary,
            trainingProgress: {
              ...prev.trainingProgress,
              currentEpoch: totalEpochs,
              timeRemaining: "Complete",
              epochs: prev.trainingProgress.epochs.map((e) => ({
                ...e,
                status: "complete",
                loss: Math.random() * 0.1 + 0.05,
              })),
              snapshots: finalSnapshots,
            },
          };
        }

        // Update progress
        const completedEpoch = nextEpoch - 1;
        const updatedEpochs = prev.trainingProgress.epochs.map((e) => {
          if (e.epoch < nextEpoch) {
            return {
              ...e,
              status: "complete" as const,
              loss: Math.random() * 0.1 + 0.05,
            };
          }
          if (e.epoch === nextEpoch) {
            return { ...e, status: "running" as const };
          }
          return e;
        });

        // Check if we should generate a snapshot at this epoch
        const snapshotInterval = prev.trainingProgress.snapshotInterval;
        let updatedSnapshots = prev.trainingProgress.snapshots;

        if (completedEpoch > 0 && completedEpoch % snapshotInterval === 0) {
          const newSnapshot: EpochSnapshot = {
            epoch: completedEpoch,
            timestamp: new Date(),
            loss: Math.random() * 0.08 + 0.04, // Lower loss as training progresses
            previewImages: getSnapshotImages(),
          };
          updatedSnapshots = [...prev.trainingProgress.snapshots, newSnapshot];
        }

        return {
          ...prev,
          trainingProgress: {
            ...prev.trainingProgress,
            currentEpoch: nextEpoch,
            timeRemaining: calculateTimeRemaining(totalEpochs, nextEpoch - 1),
            epochs: updatedEpochs,
            snapshots: updatedSnapshots,
          },
        };
      });
    }, epochDuration);
  }, [castName]);

  const cancelTraining = useCallback(() => {
    if (trainingIntervalRef.current) {
      clearInterval(trainingIntervalRef.current);
    }
    // Reset to initial state
    setState(initialState);
  }, []);

  // Slide 6: Testing
  const regenerateTest = useCallback((testId: string) => {
    setState((prev) => ({
      ...prev,
      testResults: prev.testResults.map((test) =>
        test.id === testId
          ? {
              ...test,
              score: Math.floor(Math.random() * 15) + 80,
              accepted: false,
            }
          : test,
      ),
    }));
  }, []);

  const acceptTest = useCallback((testId: string) => {
    setState((prev) => ({
      ...prev,
      testResults: prev.testResults.map((test) =>
        test.id === testId ? { ...test, accepted: true } : test,
      ),
    }));
  }, []);

  // Slide 7: Lock
  const setTriggerWord = useCallback((word: string) => {
    setState((prev) => {
      if (!prev.trainingSummary) return prev;
      return {
        ...prev,
        trainingSummary: { ...prev.trainingSummary, triggerWord: word },
      };
    });
  }, []);

  const lockLora = useCallback(() => {
    setState((prev) => ({ ...prev, isComplete: true }));
    // In real implementation, this would call API to lock LoRA
  }, []);

  const saveAsDraft = useCallback(() => {
    setState((prev) => ({ ...prev, isComplete: true }));
    // In real implementation, this would call API to save as draft
  }, []);

  // Reset wizard
  const resetWizard = useCallback(() => {
    if (trainingIntervalRef.current) {
      clearInterval(trainingIntervalRef.current);
    }
    // Cleanup preview URLs
    state.references.forEach((ref) => URL.revokeObjectURL(ref.previewUrl));
    setState(initialState);
  }, [state.references]);

  return {
    ...state,
    nextSlide,
    prevSlide,
    goToSlide,
    selectModel,
    addReferences,
    removeReference,
    updateReferenceDescription,
    toggleReferenceTriggerWord,
    updateSettings,
    updateAugmentation,
    startTraining,
    cancelTraining,
    regenerateTest,
    acceptTest,
    setTriggerWord,
    lockLora,
    saveAsDraft,
    resetWizard,
  };
}

// Helper to calculate time remaining
function calculateTimeRemaining(
  totalEpochs: number,
  completedEpochs: number,
): string {
  const remainingEpochs = totalEpochs - completedEpochs;
  // Assume 2 seconds per epoch for demo
  const remainingSeconds = remainingEpochs * 2;

  if (remainingSeconds < 60) {
    return `${remainingSeconds}s`;
  }

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  return `${minutes}m ${seconds}s`;
}
